import { walk } from '../utils';
export function stripLoneBreaks(root) {
  let nodesToSkip = ['UL', 'OL'];
  walk(root, nodesToSkip, node => {
    if (node.nodeName === 'BR') {
      if (node.parentNode?.childNodes.length === 1) {
        node.parentNode?.replaceChild(document.createTextNode(''), node);
      }
    }
  });
  return root;
}

// TextExpander allows images to be inline but we treat them as blocks
// That means if you put line breaks between images in a TextExpander snippet
// a <br> is inserted when you paste the snippet. We don't want that <br>
// because the fact that images are block-level means they go on a new line anyway
// so the <br> adds too much spacing.
// It's not possible to detect when a TextExpander snippet is pasted, so this is applied
// to all pasted HTML.
export function stripTopLevelBreaksBetweenImages(root) {
  let nodesToSkip = ['UL', 'OL', 'TABLE'];
  let nodesToRemove = [];
  walk(root, nodesToSkip, node => {
    let isTopLevel = node.parentNode === root;
    let hasImgSibling = node.previousSibling?.nodeName === 'IMG' || node.nextSibling?.nodeName === 'IMG';
    if (node.nodeName === 'BR' && isTopLevel && hasImgSibling) {
      nodesToRemove.push(node);
    }
  });
  nodesToRemove.forEach(node => node.parentNode?.removeChild(node));
  return root;
}

// Some users save TextExpander snippets as rich text, so they're inserted as HTML content
// When these snippets contain markdown syntax for images, the link can be represented by an <a> element or plain text.
// We need to find these and create an image node to replace the markdown.
// E.g.
//   given the plaintext: `![alt](link-to-my-image.com)`
//   the HTML version can be: `![alt](<a src="link-to-my-image.com">link-to-my-image.com</a>)`
//   or: `![alt](link-to-my-image.com)`
export function parseMarkdownImageSyntax(root) {
  let nodesToSkip = ['UL', 'OL', 'TABLE'];
  walk(root, nodesToSkip, node => {
    if (node.nodeName === '#text') {
      let partialMarkdownSyntaxMatch = node.nodeValue?.match(/(?:[\s\S])?^\!\[(.+?)\]\(/);
      if (partialMarkdownSyntaxMatch) {
        // We have found the start of markdown syntax of the form `![alt](`

        let siblingIsAnchor = node.nextSibling?.nodeName === 'A';
        if (siblingIsAnchor) {
          // The image URL is in the form of an <a> element
          // Next check is that </a> is followed immediately by a closing parenthesis )
          let anchor = node.nextSibling;
          let boundedByParenthesis = anchor.nextSibling?.nodeValue?.startsWith(')');
          if (anchor.nextSibling && boundedByParenthesis) {
            // We have matched to content like `![alt text](<a href="url">url</a>)`
            replaceAnchorWithImage(partialMarkdownSyntaxMatch, anchor, node);
          }
        } else {
          // It still might be markdown syntax but the image URL is a string so the entire markdown text will be within the node
          let fullMarkdownSyntaxMatch = node.nodeValue?.match(/(?:[\s\S])?^\!\[(.+?)\]\((.+?)(?:\s.*?)?\)/);
          if (fullMarkdownSyntaxMatch) {
            // We've matched on the format `![alt text](url)`
            replaceTextWithImage(fullMarkdownSyntaxMatch, node);
          }
        }
      }
    }
  });
  return root;
}

// we only support top level (block) images, so will drop any images
// which are embedded in other content.
// this fixes that by detecting any images that aren't at the root
// and "yanks" them up to the top level, splitting the document
// where we find them so any text before/after the image ends up
// in the right place.
//
// ```html
//   <p>some text <img> more text</p>
// ```
// becomes
// ```html
//   <p>some text</p>
//   <img>
//   <p> more text </p>
// ```
//
// Known issue - the containing element is left in place, so can
// cause errant paragraphs once parsed.
//
// ```html
//   <p><img></p>
// ```
// becomes
// ```html
//   <p></p>
//   <img>
//   <p></p>
// ```
export function yankImagesUpToRoot(root) {
  let nodesToSkip = ['UL', 'OL', 'TABLE'];
  walk(root, nodesToSkip, node => {
    if (node.nodeName === 'IMG') {
      let img = node;
      let parentNode = img.parentNode;
      if (parentNode && parentNode !== root) {
        // create a range that starts just after the <img> tag
        // and ends at the end of the document
        let rangeStart = [...parentNode.children].indexOf(img) + 1;
        let range = document.createRange();
        range.setStart(parentNode, rangeStart);
        range.setEnd(root, root.childNodes.length);

        // extract that range so it's no longer in the dom
        let fragment = range.extractContents();

        // move the img to the root so it's no longer nested
        root.appendChild(img);

        // add back the rest of the document after the img
        root.appendChild(fragment);
      }
    }
  });
  return root;
}

// Sometimes we get HTML which has A tags with block children, eg:
//
// <a href="foo">
//   <div>
//     <span>some text</span>
//   </div>
//   <div>
//     <span>some more text</span>
//   </div>
// </a>
//
// That results in us not correctly adding the link to the text.
// We "fix" that by cloning the A tag inside the block elements
// so that it can be detected.
//
// <div>
//   <a href="foo">
//     <span>some text</span>
//   </a>
// </div>
// <div>
//   <a href="foo">
//     <span>some more text</span>
//   </a>
// </div>
//
export function fixAnchorsAroundBlockElements(root) {
  let context = {
    inLink: null,
    addedInnerLink: false
  };
  recurse(root, (node, processChildNodes) => {
    if (node.tagName === 'A' && !context.inLink) {
      context.inLink = node;
      processChildNodes();
      context.inLink = null;
      if (context.addedInnerLink) {
        let parent = node.parentNode;
        node.childNodes.forEach(child => {
          parent?.appendChild(child);
        });
        parent?.removeChild(node);
      }
    } else if (isBlock(node) && context.inLink) {
      context.addedInnerLink = true;
      let a = context.inLink.cloneNode();
      node.childNodes.forEach(child => {
        a.appendChild(child);
      });
      node.appendChild(a);
      processChildNodes();
    } else {
      processChildNodes();
    }
  });
  return root;
}
function isBlock(node) {
  return ['DIV', 'H1', 'H2', 'H3', 'H4'].includes(node.tagName);
}
function recurse(node, fn) {
  fn(node, () => {
    [...node.children].forEach(childNode => {
      recurse(childNode, fn);
    });
  });
}
function createImageElement(altText, url) {
  let imageElement = document.createElement('img');
  imageElement.src = url;
  imageElement.alt = altText;
  imageElement.setAttribute('data-link-url', url);
  return imageElement;
}
function replaceAnchorWithImage(textMatch, anchorNode, baseNode) {
  let altText = textMatch[1];
  let imageUrl = anchorNode.href;
  let imageElement = createImageElement(altText, imageUrl);
  let suffixText = anchorNode.nextSibling?.nodeValue?.slice(1) || '';

  // Working backwards from the enclosing ), strip out the markdown syntax and replace the anchor with an image element
  if (anchorNode.nextSibling) {
    baseNode.parentNode?.replaceChild(document.createTextNode(suffixText), anchorNode.nextSibling);
  }
  baseNode.parentNode?.replaceChild(imageElement, anchorNode);
  baseNode.parentNode?.replaceChild(document.createTextNode(''), baseNode);
}
function replaceTextWithImage(textMatch, baseNode) {
  let altText = textMatch[1];
  let imageUrl = textMatch[2];
  let imageElement = createImageElement(altText, imageUrl);
  let suffixText = baseNode.nodeValue?.replace(textMatch[0], '') || '';

  // Replace single text node with 2 nodes, one of the image and one containing any other text
  baseNode.parentNode?.insertBefore(imageElement, baseNode);
  baseNode.parentNode?.replaceChild(document.createTextNode(suffixText), baseNode);
}